import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle, User, Clock, Smile, Trash2, MoreVertical, Paperclip, Image, Coffee, Pizza, Phone, Video } from 'lucide-react';
import { useMessaging } from '../hooks/useMessaging';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import api from '../services/api';
import { soundManager } from '../utils/soundManager';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number | null;
  onStartVideoCall?: (userId: number, callType: 'audio' | 'video', callId: number, roomName: string) => void;
}

const MessageModal: React.FC<MessageModalProps> = ({ isOpen, onClose, userId, onStartVideoCall }) => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [userScrolled, setUserScrolled] = useState(false);
  const [showMessageMenu, setShowMessageMenu] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const uploadingRef = useRef(false);
  const [imageModal, setImageModal] = useState<{src: string, name: string} | null>(null);
  const [editingMessage, setEditingMessage] = useState<{id: number, text: string} | null>(null);
  const [showReactions, setShowReactions] = useState<number | null>(null);

  const quickReactions = ['❤️', '😂', '😍', '😢', '😡', '👍', '👎', '😮', '🎉', '🔥'];
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { 
    sendMessage, 
    getConversation, 
    markAllAsRead, 
    markAsRead,
    onlineUsers,
    allUsers,
    loadMessages
  } = useMessaging();

  const selectedUser = allUsers.find(u => u.id === userId);
  const conversation = userId ? getConversation(userId) : [];


  useEffect(() => {
    if (isOpen && userId) {
      // Mark all messages from this user as read
      markAllAsRead(userId);
    }
  }, [isOpen, userId, markAllAsRead]);

  // Yeni mesaj geldiğinde de okundu olarak işaretle (chat açıkken)
  useEffect(() => {
    if (isOpen && userId && conversation.length > 0) {
      const hasUnread = conversation.some(msg =>
        msg.sender_id === userId && msg.receiver_id === user?.id && !msg.is_read
      );
      if (hasUnread) {
        markAllAsRead(userId);
      }
    }
  }, [isOpen, userId, conversation, user, markAllAsRead]);

  useEffect(() => {
    // Only auto-scroll if user hasn't manually scrolled up
    if (!userScrolled && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation, userScrolled]);

  // Handle scroll detection
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setUserScrolled(!isAtBottom);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    const handleGlobalPaste = (e: ClipboardEvent) => {
      if (!isOpen) return;
      
      const items = Array.from(e.clipboardData?.items || []);
      const imageItem = items.find(item => item.type.startsWith('image/'));
      
      if (imageItem) {
        e.preventDefault();
        e.stopPropagation();
        const file = imageItem.getAsFile();
        if (file) {
          // Görsel dosya olarak yüklenip mesaj olarak gönderilir
          handleFileUpload(file);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('paste', handleGlobalPaste);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('paste', handleGlobalPaste);
    };
  }, [isOpen]);


  const handleSendMessage = async () => {
    if (!message.trim() || !userId || isLoading) return;

    setIsLoading(true);
    try {
      await sendMessage(userId, message.trim());
      setMessage('');
      // Scroll to bottom after sending message
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
      return;
    }
    // Kontrol tuşlarını atla, sadece yazma tuşlarında ses çal
    const skipKeys = ['Enter', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab',
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Escape', 'F1', 'F2',
      'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'];
    if (!skipKeys.includes(e.key)) {
      soundManager.playTypingSound();
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
    
    if (!userId) return;
    
    // Start typing indicator
    try {
      await fetch(`${API_BASE_URL}/typing/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ receiverId: userId })
      });
    } catch (error) {
      console.error('Start typing error:', error);
    }
    
    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    // Stop typing after 2 seconds of inactivity
    const timeout = setTimeout(async () => {
      try {
        await fetch(`${API_BASE_URL}/typing/stop`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({ receiverId: userId })
        });
      } catch (error) {
        console.error('Stop typing error:', error);
      }
    }, 2000);
    
    setTypingTimeout(timeout);
  };

  const emojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
    '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
    '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
    '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
    '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬',
    '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
    '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯',
    '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐',
    '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈',
    '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉',
    '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '👏',
    '🙌', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🦿', '🦵', '🦶',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
    '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
    '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐',
    '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐',
    '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳'
  ];

  const addEmoji = (emoji: string) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Delete message
  const deleteMessage = async (messageId: number) => {
    if (!confirm('Mesajı silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        loadMessages();
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // Edit message
  const editMessage = async (messageId: number, newText: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ message: newText })
      });
      
      if (response.ok) {
        setEditingMessage(null);
        loadMessages();
      }
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  // Start video call
  const startVideoCall = async () => {
    if (!userId || !onStartVideoCall) return;

    try {
      const response = await api.post('/calls/start', {
        targetUserId: userId,
        callType: 'video',
        isGroupCall: false
      });

      if (response && response.success) {
        // Call the parent callback to open the App-level VideoCallModal
        onStartVideoCall(userId, 'video', response.callId, response.roomName);
      }
    } catch (error: any) {
      console.error('Error starting video call:', error);
      alert(error?.message || 'Görüntülü arama başlatılamadı');
    }
  };

  // Start audio call
  const startAudioCall = async () => {
    if (!userId || !onStartVideoCall) return;

    try {
      const response = await api.post('/calls/start', {
        targetUserId: userId,
        callType: 'audio',
        isGroupCall: false
      });

      if (response && response.success) {
        // Call the parent callback to open the App-level VideoCallModal
        onStartVideoCall(userId, 'audio', response.callId, response.roomName);
      }
    } catch (error: any) {
      console.error('Error starting audio call:', error);
      alert(error?.message || 'Sesli arama başlatılamadı');
    }
  };

  // Clear all conversation
  const clearConversation = async () => {
    if (!confirm('Tüm sohbeti silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/messages/conversation/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        // Trigger message refresh
        window.dispatchEvent(new CustomEvent('refreshMessages'));
      }
    } catch (error) {
      console.error('Error clearing conversation:', error);
    }
  };

  // Handle file upload (yapıştırılan ekran görüntüleri dahil; görseller sohbette önizlenir)
  const handleFileUpload = async (file: File) => {
    if (!userId) return;
    // Yükleme sürerken tekrar yapıştırma/bırakma aynı dosyayı çoğaltmasın
    if (uploadingRef.current) return;
    
    // 50MB limit check
    if (file.size > 50 * 1024 * 1024) {
      alert('Dosya boyutu 50MB\'dan büyük olamaz!');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('receiverId', userId.toString());
    
    uploadingRef.current = true;
    try {
      const response = await fetch(`${API_BASE_URL}/messages/file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        loadMessages();
      } else {
        alert('Dosya yüklenemedi!');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Dosya yüklenirken hata oluştu!');
    } finally {
      uploadingRef.current = false;
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide if leaving the container, not child elements
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  // Handle paste (for screenshots)
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    
    if (imageItem) {
      const file = imageItem.getAsFile();
      if (file) {
        handleFileUpload(file);
      }
    }
  };

  // Add reaction to message
  const addReaction = async (messageId: number, emoji: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/messages/${messageId}/reaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ emoji })
      });
      
      if (response.ok) {
        loadMessages();
        setShowReactions(null);
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  // Remove reaction from message
  const removeReaction = async (messageId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/messages/${messageId}/reaction`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        loadMessages();
      }
    } catch (error) {
      console.error('Error removing reaction:', error);
    }
  };

  // Check typing status
  useEffect(() => {
    if (!isOpen || !userId) return;
    
    const checkTyping = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/typing/status/${userId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        const data = await response.json();
        setIsTyping(data.typing);
      } catch (error) {
        console.error('Check typing error:', error);
      }
    };
    
    checkTyping();
    const interval = setInterval(checkTyping, 1000);
    
    return () => {
      clearInterval(interval);
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [isOpen, userId, typingTimeout]);

  
  if (!isOpen || !selectedUser) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg w-full sm:max-w-2xl h-[95dvh] sm:h-[600px] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <img
                className="w-8 h-8 rounded-full"
                src={selectedUser.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                alt={selectedUser.name}
              />
              <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${
                selectedUser.is_online ? 'bg-green-500' : 'bg-gray-400'
              }`}></div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {selectedUser.name}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                {selectedUser.role} • {selectedUser.is_online ? 'Çevrimiçi' : 
                  `Son görülme: ${new Date(selectedUser.last_login).toLocaleString('tr-TR', {
                    day: '2-digit',
                    month: '2-digit', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={startAudioCall}
              className="p-2 text-gray-400 hover:text-green-500 transition-colors"
              title="Sesli Arama"
            >
              <Phone className="w-5 h-5" />
            </button>
            <button
              onClick={startVideoCall}
              className="p-2 text-gray-400 hover:text-primary transition-colors"
              title="Görüntülü Arama"
            >
              <Video className="w-5 h-5" />
            </button>
            <button
              onClick={clearConversation}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              title="Sohbeti Temizle"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>


        {/* Messages */}
        <div 
          ref={messagesContainerRef}
          className={`flex-1 overflow-y-auto p-4 space-y-4 relative ${dragOver ? 'bg-primary/10 dark:bg-primary/20' : ''}`}
          onScroll={handleScroll}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {dragOver && (
            <div className="absolute inset-0 flex items-center justify-center bg-primary/20 dark:bg-primary/50 border-2 border-dashed border-primary rounded-lg z-10">
              <div className="text-center">
                <Paperclip className="w-12 h-12 text-primary mx-auto mb-2" />
                <p className="text-primary dark:text-primary font-medium">Dosyayı buraya bırakın</p>
              </div>
            </div>
          )}
          {conversation.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Henüz mesaj yok. İlk mesajı gönderin!
              </p>
            </div>
          ) : (
            conversation.map((msg) => {
              const isOwn = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex items-start space-x-2 ${isOwn ? 'flex-row-reverse space-x-reverse' : ''}`}
                >
                  {/* Avatar */}
                  <img
                    className="w-8 h-8 rounded-full flex-shrink-0"
                    src={isOwn 
                      ? (user?.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop')
                      : (selectedUser.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop')
                    }
                    alt={isOwn ? user?.name : selectedUser.name}
                  />
                  
                  <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-md`}>
                    {/* Sender name */}
                    <div className={`text-xs text-gray-500 dark:text-gray-400 mb-1 px-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                      {isOwn ? 'Sen' : selectedUser.name}
                    </div>
                    
                    {/* Message bubble */}
                    <div className="relative group">
                      <div
                        className={`px-4 py-2 rounded-2xl max-w-full break-words ${
                          isOwn
                            ? 'bg-primary text-white rounded-br-md'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-md'
                        }`}
                      >
                        {msg.message_type === 'system' && msg.call_status ? (
                          <div className="flex items-center gap-2">
                            {msg.call_type === 'video' ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            )}
                            <span className={`text-sm ${
                              msg.call_status === 'missed' ? 'text-red-600 dark:text-red-400 font-medium' :
                              msg.call_status === 'rejected' ? 'text-orange-600 dark:text-orange-400' :
                              msg.call_status === 'ended' ? 'text-green-600 dark:text-green-400' :
                              'text-primary dark:text-primary'
                            }`}>
                              {msg.message}
                            </span>
                          </div>
                        ) : msg.message.startsWith('[Görsel:') ? (
                          <div>
                            <p className="text-xs text-gray-400 mb-2">
                              {msg.message.split('] ')[0] + ']'}
                            </p>
                            <img 
                              src={msg.message.split('] ')[1]} 
                              alt="Paylaşılan görsel"
                              className="max-w-xs max-h-64 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setImageModal({
                                src: msg.message.split('] ')[1],
                                name: msg.message.split('] ')[0].replace('[Görsel: ', '').replace(']', '')
                              })}
                            />
                          </div>
                        ) : msg.message.startsWith('[Dosya:') ? (
                          <div>
                            <p className="text-xs text-gray-400 mb-2">
                              {msg.message.split('] ')[0] + ']'}
                            </p>
                            <a 
                              href={msg.message.split('] ')[1]}
                              download
                              className="inline-flex items-center px-3 py-2 bg-primary/20 dark:bg-primary/90 text-primary-dark dark:text-accent rounded-lg hover:bg-primary/30 dark:hover:bg-primary-dark transition-colors"
                            >
                              📄 {msg.message.split('] ')[0].replace('[Dosya: ', '').replace(']', '')}
                            </a>
                          </div>
                        ) : editingMessage?.id === msg.id ? (
                          <div className="w-full">
                            <input
                              type="text"
                              value={editingMessage.text}
                              onChange={(e) => setEditingMessage({...editingMessage, text: e.target.value})}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  editMessage(msg.id, editingMessage.text);
                                } else if (e.key === 'Escape') {
                                  setEditingMessage(null);
                                }
                              }}
                              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                              autoFocus
                            />
                            <div className="flex space-x-2 mt-1">
                              <button
                                onClick={() => editMessage(msg.id, editingMessage.text)}
                                className="text-xs text-primary hover:text-primary-dark"
                              >
                                Kaydet
                              </button>
                              <button
                                onClick={() => setEditingMessage(null)}
                                className="text-xs text-gray-600 hover:text-gray-800"
                              >
                                İptal
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm leading-relaxed">{msg.message}</p>
                            {msg.edited_at && (
                              <p className="text-xs text-gray-400 mt-1 italic">düzenlendi</p>
                            )}
                          </div>
                        )}
                      </div>
                      

                      
                      {/* Reactions */}
                      {msg.reactions && msg.reactions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(
                            msg.reactions.reduce((acc: any, r: any) => {
                              if (!acc[r.emoji]) acc[r.emoji] = [];
                              acc[r.emoji].push(r);
                              return acc;
                            }, {})
                          ).map(([emoji, reactions]: [string, any]) => {
                            const hasUserReaction = reactions.some((r: any) => r.user_id === user?.id);
                            return (
                              <button
                                key={emoji}
                                onClick={() => hasUserReaction ? removeReaction(msg.id) : addReaction(msg.id, emoji)}
                                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                  hasUserReaction 
                                    ? 'bg-primary/20 dark:bg-primary/90 border-primary/50 dark:border-primary-dark' 
                                    : 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                                title={reactions.map((r: any) => r.user_name).join(', ')}
                              >
                                {emoji} {reactions.length}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Quick reaction button for received messages */}
                      {!isOwn && (
                        <div className="absolute -right-8 top-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setShowReactions(showReactions === msg.id ? null : msg.id)}
                            className="p-1 text-gray-400 hover:text-yellow-500 transition-colors"
                            title="Tepki ver"
                          >
                            😊
                          </button>
                          
                          {showReactions === msg.id && (
                            <div className="absolute right-0 top-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 z-20 flex gap-1">
                              {quickReactions.map(emoji => (
                                <button
                                  key={emoji}
                                  onClick={() => addReaction(msg.id, emoji)}
                                  className="text-lg hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 transition-colors"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Message menu for own messages */}
                      {isOwn && (
                        <div className="absolute -right-8 top-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setShowMessageMenu(showMessageMenu === msg.id ? null : msg.id)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          
                          {showMessageMenu === msg.id && (
                            <div className="absolute right-0 top-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-20">
                              <button
                                onClick={() => {
                                  setEditingMessage({id: msg.id, text: msg.message});
                                  setShowMessageMenu(null);
                                }}
                                className="flex items-center px-3 py-2 text-sm text-primary hover:bg-primary/10 dark:hover:bg-primary/20 w-full text-left"
                              >
                                ✏️ Düzenle
                              </button>
                              <button
                                onClick={() => {
                                  deleteMessage(msg.id);
                                  setShowMessageMenu(null);
                                }}
                                className="flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Sil
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Time and read status */}
                    <div className={`flex items-center mt-1 px-1 ${isOwn ? 'flex-row-reverse' : ''}`}>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(msg.created_at).toLocaleTimeString('tr-TR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {isOwn && (
                        <span className={`text-xs ml-1 ${
                          msg.is_read ? 'text-primary' : 'text-gray-400'
                        }`}>
                          {msg.is_read ? '✓✓' : '✓'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {/* Typing indicator */}
          {isTyping && (
            <div className="flex items-start space-x-2">
              <img
                className="w-8 h-8 rounded-full flex-shrink-0"
                src={selectedUser.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                alt={selectedUser.name}
              />
              <div className="flex flex-col items-start">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 px-1">
                  {selectedUser.name}
                </div>
                <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-2xl rounded-bl-md">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
          
          {/* Scroll to bottom button */}
          {userScrolled && (
            <div className="absolute bottom-4 right-4 z-10">
              <button
                onClick={() => {
                  setUserScrolled(false);
                  messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-primary text-white p-2 rounded-full shadow-lg hover:bg-primary-dark transition-colors"
                title="En alta git"
              >
                ↓
              </button>
            </div>
          )}
        </div>

        {/* Message Input */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="relative">
            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div 
                ref={emojiPickerRef}
                className="absolute bottom-16 left-0 right-0 sm:right-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 sm:p-4 sm:w-80 max-h-52 overflow-y-auto z-10"
              >
                <div className="grid grid-cols-10 gap-2">
                  {emojis.map((emoji, index) => (
                    <button
                      key={index}
                      onClick={() => addEmoji(emoji)}
                      className="text-xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 transition-colors"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-12 h-12 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center"
              >
                <Smile className="w-5 h-5" />
              </button>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-12 h-12 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center"
                title="Dosya Ekle"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
              />
              
              <input
                type="text"
                value={message}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Mesaj yazın veya dosya yapıştırın..."
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!message.trim() || isLoading}
                className="w-12 h-12 bg-primary text-white rounded-full hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Image Modal */}
      {imageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70]" onClick={() => setImageModal(null)}>
          <div className="relative max-w-4xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setImageModal(null)}
              className="absolute -top-2 -right-2 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                const link = document.createElement('a');
                link.href = imageModal.src;
                link.download = imageModal.name;
                link.click();
              }}
              className="absolute top-12 -right-2 bg-primary text-white rounded-full p-2 hover:bg-primary-dark transition-colors z-10"
              title="İndir"
            >
              ↓
            </button>
            <img
              src={imageModal.src}
              alt={imageModal.name}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageModal;
